/**
 * Nutrition Sponsor dashboard.
 * NON-FINANCIAL: nutrition *awareness* and *education* only — no sponsorship
 * amounts, packages, payments, donations or personalised prescriptions.
 * In-page section nav; real data via existing endpoints; honest empty states.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard, BookOpen, Library, Megaphone, Users, MessagesSquare,
  Activity, User, Bell, Bot, ArrowRight, BookMarked, Apple, Salad,
  ShieldCheck, HeartPulse, MapPin,
} from 'lucide-react'
import { GovLayout, GovPageTitle, GovCard, GovButton } from '@/components/layout/GovLayout'
import { FloatingTbAssistant } from '@/components/portal/FloatingTbAssistant'
import { SupporterRoleNav, type SupporterNavSection } from '@/components/supporter/SupporterRoleNav'
import {
  SupporterStatCard, SupporterSectionCard, SupporterResourceCard,
  SupporterCampaignCard, SupporterEmptyState,
} from '@/components/supporter/SupporterCards'
import { SupporterProfilePanel } from '@/components/supporter/SupporterProfilePanel'
import { SupporterCollaborationForm } from '@/components/supporter/SupporterCollaborationForm'
import { useAuthStore } from '@/stores/authStore'
import notificationService, { type AppNotification } from '@/services/notificationService'
import {
  saveResource, unsaveResource, getSavedResources,
  type Resource, type SavedResourceItem,
} from '@/services/resourceService'
import campaignService, { type Campaign } from '@/services/campaignService'
import {
  useSupporterActivity, fetchActiveCampaigns, fetchResources,
  shareLink, campaignShareUrl, resourceShareUrl,
} from './supporterData'
import {
  NUTRITION_CAMPAIGN_TYPES, NUTRITION_DISCLAIMER, MEDICAL_DISCLAIMER,
  IMPACT_DISCLAIMER, COLLABORATION_OPTIONS,
} from './supporterContent'

const SECTIONS: SupporterNavSection[] = [
  { key: 'overview', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'learning', label: 'Nutrition Learning', icon: BookOpen },
  { key: 'hub', label: 'Knowledge Hub', icon: Library },
  { key: 'campaigns', label: 'Awareness Campaigns', icon: Megaphone },
  { key: 'resources', label: 'Resources', icon: Library },
  { key: 'community', label: 'Community', icon: Users },
  { key: 'collaboration', label: 'Collaboration', icon: MessagesSquare },
  { key: 'activity', label: 'My Activity', icon: Activity },
  { key: 'notifications', label: 'Notifications', icon: Bell },
  { key: 'assistant', label: 'TB Assistant', icon: Bot },
  { key: 'profile', label: 'My Profile', icon: User },
]

// Knowledge-hub categories — map to resource topic/awareness browsing.
const HUB_CATEGORIES = [
  { label: 'TB & Nutrition', topic: 'NUTRITION', icon: Apple },
  { label: 'Family Nutrition', topic: 'NUTRITION', icon: HeartPulse },
  { label: 'Food Safety', topic: 'PREVENTION', icon: ShieldCheck },
  { label: 'Healthy Habits', topic: 'AWARENESS', icon: Salad },
  { label: 'Myths & Facts', topic: 'AWARENESS', icon: BookOpen },
  { label: 'Regional Resources', topic: 'NUTRITION', icon: MapPin },
  { label: 'Expert Guidance', topic: 'NUTRITION', icon: BookMarked },
]

const NutritionSponsorDashboard = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const activity = useSupporterActivity()

  const [active, setActive] = useState('overview')
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [resources, setResources] = useState<Resource[]>([])
  const [saved, setSaved] = useState<SavedResourceItem[]>([])
  const [savedIds, setSavedIds] = useState<Set<number>>(new Set())
  const [joinedIds, setJoinedIds] = useState<Set<number>>(new Set())
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [loadingList, setLoadingList] = useState(true)

  const fullName = user?.fullName || (user ? `${user.firstName} ${user.lastName}`.trim() : '') || t('supporter.hero.friend', 'friend')

  useEffect(() => {
    let active0 = true
    setLoadingList(true)
    Promise.allSettled([
      fetchActiveCampaigns(NUTRITION_CAMPAIGN_TYPES, 12),
      fetchResources({ topic: 'NUTRITION', size: 8 }),
      campaignService.getMyCampaigns({ size: 50 }),
      fetchSaved(),
    ]).then(([c, r, mine]) => {
      if (!active0) return
      setCampaigns(c.status === 'fulfilled' ? c.value : [])
      setResources(r.status === 'fulfilled' ? r.value : [])
      if (mine.status === 'fulfilled') setJoinedIds(new Set((mine.value.content ?? []).map((cm) => cm.id)))
      setLoadingList(false)
    })
    return () => { active0 = false }
  }, [])

  const fetchSaved = useCallback(async () => {
    try {
      const page = await getSavedResources(0, 50)
      const items = page.content ?? []
      setSaved(items)
      setSavedIds(new Set(items.map((i) => i.id)))
    } catch {
      setSaved([])
    }
  }, [])

  useEffect(() => {
    if (active !== 'notifications') return
    notificationService.getNotifications(0, 20).then((p) => setNotifications(p.content ?? [])).catch(() => setNotifications([]))
  }, [active])

  const toggleSave = useCallback(async (r: Resource) => {
    setSavedIds((prev) => {
      const next = new Set(prev)
      if (next.has(r.id)) next.delete(r.id)
      else next.add(r.id)
      return next
    })
    try {
      if (savedIds.has(r.id)) await unsaveResource(r.id)
      else await saveResource(r.id)
      await fetchSaved()
    } catch {
      await fetchSaved()
    }
  }, [savedIds, fetchSaved])

  const joinCampaign = useCallback(async (c: Campaign) => {
    setJoinedIds((prev) => new Set(prev).add(c.id))
    try { await campaignService.participate(c.id) } catch { setJoinedIds((prev) => { const n = new Set(prev); n.delete(c.id); return n }) }
  }, [])

  const onShareCampaign = useCallback((c: Campaign) => { shareLink(campaignShareUrl(c), c.title) }, [])
  const onShareResource = useCallback((r: Resource) => { shareLink(resourceShareUrl(r), r.title) }, [])

  const statCards = useMemo(() => [
    { icon: BookMarked, value: activity.resourcesSaved, labelKey: 'supporter.nutrition.stats.resourcesSaved', label: 'Resources Saved', accent: 'bg-emerald-50 text-emerald-600' },
    { icon: Megaphone, value: activity.campaignsJoined, labelKey: 'supporter.nutrition.stats.campaignsJoined', label: 'Awareness Campaigns Joined', accent: 'bg-blue-50 text-blue-600' },
    { icon: BookOpen, value: 0, labelKey: 'supporter.nutrition.stats.modulesCompleted', label: 'Learning Modules Completed', dataRequired: true, accent: 'bg-cyan-50 text-cyan-600' },
    { icon: Users, value: 0, labelKey: 'supporter.nutrition.stats.communityReach', label: 'Community Activities', dataRequired: true, accent: 'bg-violet-50 text-violet-600' },
  ], [activity])

  const primaryActions = [
    { label: t('supporter.nutrition.actionLearn', 'Start Nutrition Learning'), icon: BookOpen, onClick: () => setActive('learning') },
    { label: t('supporter.nutrition.actionHub', 'Explore Knowledge Hub'), icon: Library, onClick: () => setActive('hub') },
    { label: t('supporter.nutrition.actionCampaign', 'Join a Nutrition Campaign'), icon: Megaphone, onClick: () => setActive('campaigns') },
    { label: t('supporter.nutrition.actionResources', 'Browse Nutrition Resources'), icon: Apple, onClick: () => setActive('resources') },
  ]

  return (
    <GovLayout>
      <FloatingTbAssistant />
      <div className="space-y-5">
        <GovPageTitle
          title={t('supporter.nutrition.title', 'Welcome, {{name}}', { name: fullName })}
          subtitle={t('supporter.nutrition.subtitle', 'Promote nutrition awareness for stronger TB recovery.')}
          icon={Apple}
        />

        <GovCard>
          <div className="flex flex-wrap items-center gap-2.5">
            <GovButton variant="primary" size="sm" onClick={() => navigate('/assistant')} className="inline-flex items-center gap-1.5"><Bot className="w-4 h-4" /> {t('supporter.hero.tbAssistant', 'TB Assistant')}</GovButton>
            <GovButton variant="secondary" size="sm" onClick={() => setActive('notifications')} className="inline-flex items-center gap-1.5"><Bell className="w-4 h-4" /> {t('supporter.hero.notifications', 'Notifications')}
              {activity.unreadCount > 0 && <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full bg-red-500 text-white">{activity.unreadCount}</span>}
            </GovButton>
            <GovButton variant="secondary" size="sm" onClick={() => navigate(`/profile/${user?.username}`)} className="inline-flex items-center gap-1.5"><User className="w-4 h-4" /> {t('supporter.hero.profile', 'Profile')}</GovButton>
            <span className="text-xs text-gray-400 ml-auto">{t('supporter.nutrition.nonFinancial', 'Nutrition awareness & education — no payments or packages.')}</span>
          </div>
        </GovCard>

        <SupporterRoleNav sections={SECTIONS} active={active} onChange={setActive} />

        {/* Overview */}
        {active === 'overview' && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">{statCards.map((s) => <SupporterStatCard key={s.labelKey} {...s} />)}</div>
            <SupporterSectionCard title={t('supporter.nutrition.primaryActions', 'What you can do today')} icon={LayoutDashboard}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {primaryActions.map((a) => {
                  const Icon = a.icon
                  return (
                    <button key={a.label} onClick={a.onClick} className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 hover:bg-emerald-100 transition-colors text-left min-h-[56px]">
                      <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-emerald-600" /></div>
                      <span className="font-medium text-gray-900 text-sm flex-1 min-w-0">{a.label}</span>
                      <ArrowRight className="w-4 h-4 text-emerald-500 shrink-0" />
                    </button>
                  )
                })}
              </div>
            </SupporterSectionCard>
            <div className="grid lg:grid-cols-2 gap-4">
              <SupporterSectionCard title={t('supporter.nutrition.featuredNutrition', 'Featured nutrition resources')} icon={Apple} action={<GovButton variant="ghost" size="sm" onClick={() => setActive('resources')}>{t('supporter.actions.viewAll', 'View all')}</GovButton>}>
                {loadingList ? <SkeletonRows /> : resources.length === 0
                  ? <SupporterEmptyState icon="🥗" title={t('supporter.nutrition.emptyResources', 'Nutrition resources coming soon')} actionLabel={t('supporter.nutrition.actionHub', 'Explore Knowledge Hub')} onAction={() => setActive('hub')} />
                  : <div className="space-y-3">{resources.slice(0, 3).map((r) => <SupporterResourceCard key={r.id} resource={r} saved={savedIds.has(r.id)} onToggleSave={toggleSave} onShare={onShareResource} />)}</div>}
              </SupporterSectionCard>
              <SupporterSectionCard title={t('supporter.nutrition.activeCampaigns', 'Active nutrition awareness campaigns')} icon={Megaphone} action={<GovButton variant="ghost" size="sm" onClick={() => setActive('campaigns')}>{t('supporter.actions.viewAll', 'View all')}</GovButton>}>
                {loadingList ? <SkeletonRows /> : campaigns.length === 0
                  ? <SupporterEmptyState icon="📣" title={t('supporter.nutrition.emptyCampaigns', 'No active campaigns right now')} actionLabel={t('supporter.nutrition.actionCampaign', 'Explore Campaigns')} onAction={() => setActive('campaigns')} />
                  : <div className="space-y-3">{campaigns.slice(0, 3).map((c) => <SupporterCampaignCard key={c.id} campaign={c} joined={joinedIds.has(c.id)} onJoin={joinCampaign} onShare={onShareCampaign} onLearnMore={(cm) => navigate(`/campaigns/${cm.id}`)} />)}</div>}
              </SupporterSectionCard>
            </div>
          </div>
        )}

        {/* Nutrition Learning */}
        {active === 'learning' && (
          <SupporterSectionCard title={t('supporter.nutrition.learningTitle', 'Nutrition learning')} description={NUTRITION_DISCLAIMER} icon={BookOpen}>
            <p className="text-sm text-gray-600">{t('supporter.nutrition.learningDesc', 'Build your understanding of how nutrition supports TB care — for awareness only, not personal prescriptions.')}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {HUB_CATEGORIES.map((c) => (
                <Link key={c.label} to="/knowledge" className="px-3 py-2 rounded-full bg-gray-100 hover:bg-emerald-100 hover:text-emerald-700 text-sm text-gray-700 transition-colors min-h-[40px] flex items-center">{c.label}</Link>
              ))}
            </div>
            <div className="mt-4"><GovButton variant="secondary" size="sm" onClick={() => navigate('/knowledge')}>{t('supporter.nutrition.openKnowledge', 'Open Knowledge Hub')}<ArrowRight className="w-4 h-4 ml-1" /></GovButton></div>
          </SupporterSectionCard>
        )}

        {/* Knowledge Hub */}
        {active === 'hub' && (
          <div className="space-y-5">
            <SupporterSectionCard title={t('supporter.nutrition.hubTitle', 'Nutrition knowledge hub')} description={NUTRITION_DISCLAIMER} icon={Library}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {HUB_CATEGORIES.map((c) => {
                  const Icon = c.icon
                  return (
                    <button key={c.label} onClick={() => setActive('resources')} className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 hover:bg-emerald-50 transition-colors text-left min-h-[56px]">
                      <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-emerald-600" /></div>
                      <span className="font-medium text-gray-900 text-sm flex-1 min-w-0">{c.label}</span>
                      <ArrowRight className="w-4 h-4 text-gray-400 shrink-0" />
                    </button>
                  )
                })}
              </div>
            </SupporterSectionCard>
            <SupporterSectionCard title={t('supporter.nutrition.hubResources', 'Resources in this hub')} icon={BookMarked}>
              {loadingList ? <SkeletonRows /> : resources.length === 0
                ? <SupporterEmptyState icon="🗂️" title={t('supporter.nutrition.emptyResources', 'Nutrition resources coming soon')} />
                : <div className="grid sm:grid-cols-2 gap-3">{resources.map((r) => <SupporterResourceCard key={r.id} resource={r} saved={savedIds.has(r.id)} onToggleSave={toggleSave} onShare={onShareResource} />)}</div>}
            </SupporterSectionCard>
          </div>
        )}

        {/* Campaigns */}
        {active === 'campaigns' && (
          <SupporterSectionCard title={t('supporter.nutrition.campaignsTitle', 'Nutrition awareness campaigns')} description={t('supporter.nutrition.campaignsDesc', 'Joining means awareness participation — no payment or sponsorship.')} icon={Megaphone}>
            {loadingList ? <SkeletonRows /> : campaigns.length === 0
              ? <SupporterEmptyState icon="📣" title={t('supporter.nutrition.emptyCampaigns', 'No active campaigns right now')} actionLabel={t('supporter.nutrition.actionCampaign', 'Explore Campaigns')} onAction={() => navigate('/campaigns')} />
              : <div className="grid sm:grid-cols-2 gap-3">{campaigns.map((c) => <SupporterCampaignCard key={c.id} campaign={c} joined={joinedIds.has(c.id)} onJoin={joinCampaign} onShare={onShareCampaign} onLearnMore={(cm) => navigate(`/campaigns/${cm.id}`)} />)}</div>}
          </SupporterSectionCard>
        )}

        {/* Resources */}
        {active === 'resources' && (
          <SupporterSectionCard title={t('supporter.nutrition.resourcesTitle', 'Nutrition resources')} description={NUTRITION_DISCLAIMER} icon={Apple}>
            {loadingList ? <SkeletonRows /> : resources.length === 0
              ? <SupporterEmptyState icon="🥗" title={t('supporter.nutrition.emptyResources', 'Nutrition resources coming soon')} actionLabel={t('supporter.nutrition.actionResources', 'Browse Resources')} onAction={() => navigate('/resources')} />
              : <div className="grid sm:grid-cols-2 gap-3">{resources.map((r) => <SupporterResourceCard key={r.id} resource={r} saved={savedIds.has(r.id)} onToggleSave={toggleSave} onShare={onShareResource} />)}</div>}
            <div className="mt-4"><GovButton variant="secondary" size="sm" onClick={() => navigate('/resources')}>{t('supporter.nutrition.browseResources', 'Browse all resources')}<ArrowRight className="w-4 h-4 ml-1" /></GovButton></div>
          </SupporterSectionCard>
        )}

        {/* Community */}
        {active === 'community' && (
          <SupporterSectionCard title={t('supporter.nutrition.communityTitle', 'Community')} description={t('supporter.nutrition.communityDesc', 'Join nutrition discussions, stories and awareness activities.')} icon={Users}>
            <div className="grid sm:grid-cols-2 gap-3">
              <CommunityLink icon={MessagesSquare} label={t('supporter.nutrition.communityFeed', 'Community feed')} desc={t('supporter.nutrition.communityFeedDesc', 'Discussions, posts and stories.')} to="/feed" />
              <CommunityLink icon={Megaphone} label={t('supporter.nutrition.communityCampaigns', 'Awareness activities')} desc={t('supporter.nutrition.communityCampaignsDesc', 'Join nutrition campaigns.')} to="/campaigns" />
              <CommunityLink icon={BookOpen} label={t('supporter.nutrition.communityKnowledge', 'Knowledge hub')} desc={t('supporter.nutrition.communityKnowledgeDesc', 'Trusted TB & nutrition articles.')} to="/knowledge" />
              <CommunityLink icon={Users} label={t('supporter.nutrition.communityGroups', 'Groups')} desc={t('supporter.nutrition.communityGroupsDesc', 'Connect with support groups.')} to="/groups" />
            </div>
          </SupporterSectionCard>
        )}

        {/* Collaboration */}
        {active === 'collaboration' && (
          <SupporterSectionCard title={t('supporter.collaboration.heading', 'Collaboration')} description={t('supporter.collaboration.nutritionDesc', 'Express interest in nutrition awareness, education or community initiatives. This is not a financial sponsorship.')} icon={MessagesSquare}>
            <SupporterCollaborationForm interestOptions={COLLABORATION_OPTIONS.nutrition} />
          </SupporterSectionCard>
        )}

        {/* My Activity */}
        {active === 'activity' && (
          <div className="space-y-5">
            <SupporterSectionCard title={t('supporter.nutrition.activityTitle', 'My activity')} description={IMPACT_DISCLAIMER} icon={Activity}>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">{statCards.map((s) => <SupporterStatCard key={s.labelKey} {...s} />)}</div>
            </SupporterSectionCard>
            <SupporterSectionCard title={t('supporter.nutrition.savedTitle', 'Saved resources')} icon={BookMarked}>
              {saved.length === 0
                ? <SupporterEmptyState icon="🔖" title={t('supporter.nutrition.emptySaved', 'No saved resources yet')} description={t('supporter.nutrition.emptySavedDesc', 'Save nutrition articles and guides to find them here.')} actionLabel={t('supporter.nutrition.actionResources', 'Browse Resources')} onAction={() => setActive('resources')} />
                : <div className="grid sm:grid-cols-2 gap-3">{saved.map((r) => <SupporterResourceCard key={r.id} resource={r} saved onToggleSave={toggleSave} onShare={onShareResource} />)}</div>}
            </SupporterSectionCard>
          </div>
        )}

        {/* Notifications */}
        {active === 'notifications' && (
          <SupporterSectionCard title={t('supporter.nutrition.notificationsTitle', 'Notifications')} icon={Bell} action={<GovButton variant="ghost" size="sm" onClick={() => notificationService.markAllAsRead().then(() => setNotifications((n) => n.map((x) => ({ ...x, isRead: true }))))}>{t('supporter.nutrition.markAllRead', 'Mark all read')}</GovButton>}>
            {notifications.length === 0
              ? <SupporterEmptyState icon="🔔" title={t('supporter.nutrition.emptyNotifications', 'You\'re all caught up')} description={t('supporter.nutrition.emptyNotificationsDesc', 'No notifications right now.')} />
              : <div className="divide-y divide-gray-100">{notifications.map((n) => (
                  <div key={n.id} className={`py-3 ${n.isRead ? '' : 'bg-emerald-50/40 -mx-2 px-2 rounded-lg'}`}>
                    <p className="text-sm font-medium text-gray-900">{n.title}</p>
                    <p className="text-sm text-gray-600">{n.message}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{new Date(n.createdAt).toLocaleString('en-IN')}</p>
                  </div>
                ))}</div>}
          </SupporterSectionCard>
        )}

        {/* Assistant */}
        {active === 'assistant' && (
          <SupporterSectionCard title={t('supporter.nutrition.assistantTitle', 'TB Assistant')} description={MEDICAL_DISCLAIMER} icon={Bot}>
            <p className="text-sm text-gray-600">{t('supporter.nutrition.assistantDesc', 'Ask about TB, nutrition awareness, official resources and TB centres. The assistant does not replace a doctor or dietitian.')}</p>
            <div className="mt-4 flex flex-wrap gap-2.5">
              <GovButton variant="primary" size="sm" onClick={() => navigate('/assistant')} className="inline-flex items-center gap-1.5"><Bot className="w-4 h-4" /> {t('supporter.nutrition.openAssistant', 'Open TB Assistant')}</GovButton>
              <GovButton variant="secondary" size="sm" onClick={() => navigate('/find-tb-centre')} className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {t('supporter.nutrition.findCentre', 'Find a TB Centre')}</GovButton>
            </div>
          </SupporterSectionCard>
        )}

        {/* Profile */}
        {active === 'profile' && (
          <SupporterSectionCard title={t('supporter.nutrition.profileTitle', 'My profile')} description={t('supporter.nutrition.profileDesc', 'Manage your details and nutrition awareness interests. No medical information required.')} icon={User}>
            <SupporterProfilePanel role="nutrition" />
          </SupporterSectionCard>
        )}
      </div>
    </GovLayout>
  )
}

function SkeletonRows() {
  return <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 rounded-xl bg-gray-100 animate-pulse" />)}</div>
}

function CommunityLink({ icon: Icon, label, desc, to }: { icon: React.ComponentType<{ className?: string }>; label: string; desc: string; to: string }) {
  return (
    <Link to={to} className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors min-h-[56px]">
      <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-emerald-600" /></div>
      <div className="min-w-0 flex-1"><p className="font-medium text-gray-900 text-sm truncate">{label}</p><p className="text-xs text-gray-500 truncate">{desc}</p></div>
      <ArrowRight className="w-4 h-4 text-gray-400 shrink-0" />
    </Link>
  )
}

export default NutritionSponsorDashboard